// Olivium Governance — canonical state + mutation pipeline (pure, testable).
//
// This is the single in-memory source of truth for the seven governance
// modules (the packet's GovernanceEventProvider). It is a reducer-style,
// immutable model: every command runs the mutation pipeline, ids come from one
// counter, timestamps from the action's `now`, and no card keeps a duplicate
// authoritative copy.
//
// HONESTY: every record and every interaction result carries a `savedStatus`:
//   - saved_durable        persisted server-side (hydrated from a real source)
//   - saved_local_session  exists only in this browser session (labeled)
//   - simulated            a labeled what-if (e.g. integrity-mismatch drill)
// The model never claims durable persistence, email delivery, live AI, or real
// cryptographic sealing. Governed actions append an audit event + receipt +
// integrity block that SHARE one correlation id, so acting in one card is
// visibly reflected across the others — a real local-session governance ledger.

import type { GovModule } from './governanceCards'

export type GovRole =
  | 'Viewer' | 'Commenter' | 'Contributor' | 'Reviewer' | 'Approver' | 'Operator' | 'Governance_Admin'

export const GOV_ROLES: readonly GovRole[] = [
  'Viewer', 'Commenter', 'Contributor', 'Reviewer', 'Approver', 'Operator', 'Governance_Admin',
] as const

/** Role privilege ordering (low → high). Maps onto the numeric server model. */
export const ROLE_RANK: Record<GovRole, number> = {
  Viewer: 10, Commenter: 20, Contributor: 30, Reviewer: 40, Approver: 60, Operator: 70, Governance_Admin: 90,
}

export type SavedStatus = 'saved_durable' | 'saved_local_session' | 'simulated'
export type StatusKind = 'verified' | 'attention' | 'denied' | 'neutral' | 'identity' | 'secure' | 'structure'
export type Severity = 'info' | 'low' | 'medium' | 'high' | 'critical'
export type RecordOrigin = 'design_sample' | 'local_session' | 'durable'

export interface GovComment {
  id: string
  actor: string
  actorRole: GovRole
  body: string
  at: string
  mentions: string[]
}

export interface GovUpdate {
  id: string
  actor: string
  actorRole: GovRole
  body: string
  at: string
  kind: 'note' | 'status' | 'action'
  savedStatus: SavedStatus
}

export interface GovHistoryEntry {
  id: string
  actor: string
  action: string
  at: string
  detail?: string
  correlationId?: string
  savedStatus: SavedStatus
}

export interface GovRecord {
  id: string
  module: GovModule
  correlationId: string
  title: string
  summary: string
  status: string
  statusKind: StatusKind
  severity: Severity
  actor: string
  actorRole: GovRole
  createdAt: string
  updatedAt: string
  origin: RecordOrigin
  savedStatus: SavedStatus
  /** module-specific display fields, e.g. { source_ip, result } */
  fields: Record<string, string>
  labels: string[]
  assignee: string | null
  reviewers: string[]
  watchers: string[]
  dueDate: string | null
  comments: GovComment[]
  updates: GovUpdate[]
  history: GovHistoryEntry[]
  /** correlated record ids in other modules */
  links: string[]
}

export interface GovScope {
  organization: { id: string; name: string }
  project: { id: string; name: string }
  environment: { id: string; name: string }
  portal: { id: string; name: string }
}

export interface GovPolicy {
  id: string
  name: string
  version: number
  prior?: { id: string; name: string; version: number }
}

export interface GovToast {
  id: string
  kind: 'success' | 'attention' | 'denied' | 'info'
  message: string
  correlationId?: string
  savedStatus: SavedStatus
  at: string
}

export interface GovSimulations {
  auditWriterOutage: boolean
  integrityMismatch: boolean
  policyConflict: boolean
}

/** Honest state of a durable canonical source (audit trail, integrity chain). */
export type DurableMode = 'local' | 'auth_required' | 'durable' | 'unavailable'

export interface DurableStatus {
  mode: DurableMode
  auditCount: number
  chainValid: boolean | null
  chainMessage: string
  missingRpc: boolean
}

export interface GovState {
  scope: GovScope
  policy: GovPolicy
  records: GovRecord[]
  toasts: GovToast[]
  simulations: GovSimulations
  actor: { id: string; name: string; role: GovRole }
  /** Test-as-role override; null = act as `actor.role`. */
  viewerRole: GovRole | null
  /** Live status of the durable canonical sources; null until probed. */
  durable: DurableStatus | null
  seq: number
}

// ─────────────────────────────────────────────────────────────────────────────
// ID + time helpers (deterministic given state + action `now`)
// ─────────────────────────────────────────────────────────────────────────────

export function makeId(prefix: string, n: number): string {
  return `${prefix}_${String(n).padStart(5, '0')}`
}

function iso(now: number): string {
  return new Date(now).toISOString()
}

// ─────────────────────────────────────────────────────────────────────────────
// Permission model — deny-overrides-allow, scope inheritance, visible effective
// ─────────────────────────────────────────────────────────────────────────────

export type GovAction =
  // universal
  | 'view' | 'comment' | 'add_update' | 'assign' | 'add_reviewer' | 'set_due' | 'label'
  | 'watch' | 'share' | 'copy_link' | 'view_history' | 'view_permissions' | 'edit' | 'create' | 'analyze'
  // governed / contextual
  | 'verify' | 'acknowledge' | 'mitigate' | 'resolve' | 'escalate'
  | 'trust_device' | 'challenge' | 'suspend' | 'terminate'
  | 'edit_constraints' | 'add_recipient' | 'revoke_recipient' | 'extend' | 'revoke'
  | 'approve' | 'deny' | 'request_changes' | 'reassign'
  | 'change_policy' | 'restore_policy'

/** Minimum role rank required for an action. deny-overrides is applied on top. */
const ACTION_MIN_RANK: Record<GovAction, number> = {
  view: 10, copy_link: 10, view_history: 10, view_permissions: 10, watch: 10, analyze: 10,
  comment: 20,
  add_update: 30, label: 30, create: 30, edit: 30,
  assign: 40, add_reviewer: 40, set_due: 40, request_changes: 40, acknowledge: 40,
  share: 60, edit_constraints: 60, add_recipient: 60, verify: 40,
  approve: 60, deny: 60, mitigate: 60, resolve: 60,
  trust_device: 70, challenge: 70, suspend: 70, terminate: 70, extend: 70, revoke: 70,
  revoke_recipient: 70, escalate: 70, reassign: 70,
  change_policy: 90, restore_policy: 90,
}

export interface PermissionDecision {
  allowed: boolean
  reason: string
}

/**
 * Effective permission for a role + action. Pure. deny-overrides-allow is
 * expressed via explicit denials (e.g. Viewer can never mutate) layered over
 * the rank check, so the effective decision is always visible with a reason.
 */
export function evaluatePermission(role: GovRole, action: GovAction): PermissionDecision {
  const min = ACTION_MIN_RANK[action]
  const rank = ROLE_RANK[role]
  // Explicit deny: Viewer is strictly read-only (deny overrides any allow).
  if (role === 'Viewer' && min > ROLE_RANK.Viewer) {
    return { allowed: false, reason: 'Viewer is read-only — no write actions.' }
  }
  if (rank >= min) {
    return { allowed: true, reason: `${role} (rank ${rank}) meets the required rank ${min}.` }
  }
  return { allowed: false, reason: `Requires rank ${min}; ${role} has rank ${rank}.` }
}

/** The role the current viewer is acting as (test-as-role aware). */
export function effectiveRole(state: GovState): GovRole {
  return state.viewerRole ?? state.actor.role
}

export function can(state: GovState, action: GovAction): PermissionDecision {
  return evaluatePermission(effectiveRole(state), action)
}

// ─────────────────────────────────────────────────────────────────────────────
// Selectors
// ─────────────────────────────────────────────────────────────────────────────

export function recordsByModule(state: GovState, module: GovModule): GovRecord[] {
  return state.records.filter((r) => r.module === module)
}

export function getRecord(state: GovState, id: string): GovRecord | undefined {
  return state.records.find((r) => r.id === id)
}

export function getRecordsByCorrelationId(state: GovState, correlationId: string): GovRecord[] {
  return state.records.filter((r) => r.correlationId === correlationId)
}

export interface ModuleMetrics {
  total: number
  byKind: Record<StatusKind, number>
  local: number
  durable: number
}

export function getModuleMetrics(state: GovState, module: GovModule): ModuleMetrics {
  const recs = recordsByModule(state, module)
  const byKind: Record<StatusKind, number> = {
    verified: 0, attention: 0, denied: 0, neutral: 0, identity: 0, secure: 0, structure: 0,
  }
  let local = 0
  let durable = 0
  for (const r of recs) {
    byKind[r.statusKind] += 1
    if (r.savedStatus === 'saved_durable') durable += 1
    else local += 1
  }
  return { total: recs.length, byKind, local, durable }
}

export type ModuleHealth = 'healthy' | 'attention' | 'degraded'

export function getModuleHealth(state: GovState, module: GovModule): ModuleHealth {
  if (module === 'audit' && state.simulations.auditWriterOutage) return 'degraded'
  if (module === 'integrity' && state.simulations.integrityMismatch) return 'degraded'
  const m = getModuleMetrics(state, module)
  if (m.byKind.denied > 0) return 'degraded'
  if (m.byKind.attention > 0) return 'attention'
  return 'healthy'
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutation pipeline
// ─────────────────────────────────────────────────────────────────────────────

const KIND_STATUS: Record<StatusKind, string> = {
  verified: 'Verified', attention: 'Attention', denied: 'Denied',
  neutral: 'Neutral', identity: 'Recorded', secure: 'Secured', structure: 'Linked',
}

interface PipelineSpec {
  /** action verb for the audit/history line, e.g. 'resolve_risk' */
  action: string
  /** human summary */
  summary: string
  /** the domain module the action targets */
  targetModule: GovModule
  /** id of an existing record to patch (governed edit), if any */
  targetId?: string
  /** patch to apply to the target record */
  patch?: Partial<GovRecord>
  /** a brand-new domain record to insert (create/related), if any */
  newRecord?: NewRecordInput
  severity: Severity
  toastKind: GovToast['kind']
  /** whether a receipt + integrity block are appended (governed) */
  governed: boolean
  savedStatus: SavedStatus
}

export interface NewRecordInput {
  module: GovModule
  title: string
  summary: string
  statusKind: StatusKind
  severity?: Severity
  fields?: Record<string, string>
  correlationId?: string
}

/** Build a fresh record shell owned by the acting user. */
function newRecord(
  state: GovState, seqBase: number, offset: number, now: number,
  input: NewRecordInput, correlationId: string, savedStatus: SavedStatus,
): GovRecord {
  const id = makeId(RECORD_PREFIX[input.module], seqBase + offset)
  return {
    id, module: input.module, correlationId,
    title: input.title, summary: input.summary,
    status: KIND_STATUS[input.statusKind], statusKind: input.statusKind,
    severity: input.severity ?? 'info',
    actor: state.actor.name, actorRole: effectiveRole(state),
    createdAt: iso(now), updatedAt: iso(now),
    origin: 'local_session', savedStatus,
    fields: input.fields ?? {},
    labels: [], assignee: null, reviewers: [], watchers: [state.actor.name],
    dueDate: null, comments: [], updates: [],
    history: [{
      id: makeId('hist', seqBase + offset), actor: state.actor.name,
      action: 'created', at: iso(now), correlationId, savedStatus,
    }],
    links: [],
  }
}

const RECORD_PREFIX: Record<GovModule, string> = {
  audit: 'evt', integrity: 'blk', receipts: 'rcp', risks: 'risk',
  'access-logs': 'sess', 'secure-share': 'share', 'permission-review': 'prv',
}

/**
 * Run the mutation pipeline. Returns a NEW state. Governed actions append an
 * audit event, a receipt, and an integrity block that all share a fresh
 * correlation id, plus a toast. Lightweight actions append an audit event +
 * a history entry on the target record. Never mutates in place.
 */
function runPipeline(state: GovState, spec: PipelineSpec, now: number): GovState {
  const seqBase = state.seq + 1
  const correlationId = makeId('corr', seqBase)
  const created: GovRecord[] = []
  let records = state.records
  let offset = 0

  const actorName = state.actor.name
  const actorRole = effectiveRole(state)

  // 1. Domain change (patch existing or insert new).
  let targetId = spec.targetId
  if (spec.targetId && spec.patch) {
    records = records.map((r) => {
      if (r.id !== spec.targetId) return r
      const hist: GovHistoryEntry = {
        id: makeId('hist', seqBase + offset), actor: actorName, action: spec.action,
        at: iso(now), detail: spec.summary, correlationId, savedStatus: spec.savedStatus,
      }
      return {
        ...r, ...spec.patch,
        correlationId,
        updatedAt: iso(now),
        history: [...r.history, hist],
        links: dedupe([...r.links, ...(spec.governed ? [] : [])]),
      }
    })
    offset += 1
  } else if (spec.newRecord) {
    const rec = newRecord(state, seqBase, offset, now, spec.newRecord, correlationId, spec.savedStatus)
    created.push(rec)
    targetId = rec.id
    offset += 1
  }

  // 2. Audit event (always — the trail records everything).
  const auditBlocked = state.simulations.auditWriterOutage
  if (!auditBlocked) {
    const evt = newRecord(state, seqBase, offset, now, {
      module: 'audit',
      title: spec.action.replace(/_/g, ' '),
      summary: spec.summary,
      statusKind: spec.toastKind === 'denied' ? 'denied' : 'identity',
      severity: spec.severity,
      fields: {
        result: spec.toastKind === 'denied' ? 'denied' : 'allowed',
        source: 'local_session',
        target_module: spec.targetModule,
      },
    }, correlationId, spec.savedStatus)
    created.push(evt)
    offset += 1

    if (spec.governed) {
      // 3. Receipt.
      const rcp = newRecord(state, seqBase, offset, now, {
        module: 'receipts',
        title: `Receipt · ${spec.action.replace(/_/g, ' ')}`,
        summary: `Portable proof for ${spec.summary}`,
        statusKind: 'verified',
        severity: 'info',
        fields: { receipt_status: 'issued', bundled: 'false', delivery: 'manual' },
      }, correlationId, spec.savedStatus)
      created.push(rcp)
      offset += 1

      // 4. Integrity block.
      const mismatch = state.simulations.integrityMismatch
      const blk = newRecord(state, seqBase, offset, now, {
        module: 'integrity',
        title: `Block ${makeId('', seqBase).slice(1)}`,
        summary: mismatch ? 'Chain mismatch simulated — verification would fail' : `Chained over ${spec.summary}`,
        statusKind: mismatch ? 'denied' : 'structure',
        severity: mismatch ? 'high' : 'info',
        fields: { chain_status: mismatch ? 'mismatch' : 'linked', verified_by: mismatch ? 'none' : 'olv_verify_event_chain' },
      }, correlationId, spec.savedStatus)
      created.push(blk)
      offset += 1

      // 5. Risk recalculation: a denied or high-severity governed action raises a flag.
      if (spec.toastKind === 'denied' || spec.severity === 'high' || spec.severity === 'critical') {
        const risk = newRecord(state, seqBase, offset, now, {
          module: 'risks',
          title: `Risk · ${spec.action.replace(/_/g, ' ')}`,
          summary: `Auto-raised from ${spec.summary}`,
          statusKind: 'attention',
          severity: spec.severity,
          fields: { risk_status: 'open', origin: 'auto', requires: 'triage' },
        }, correlationId, spec.savedStatus)
        created.push(risk)
        offset += 1
      }
    }
  }

  // Cross-link created records to the target.
  const createdIds = created.map((r) => r.id)
  const linkedCreated = created.map((r) => ({
    ...r, links: dedupe([...r.links, ...createdIds.filter((id) => id !== r.id), ...(targetId ? [targetId] : [])]),
  }))
  records = records.map((r) => (r.id === targetId ? { ...r, links: dedupe([...r.links, ...createdIds]) } : r))

  // 6. Toast.
  const toast: GovToast = {
    id: makeId('toast', seqBase + offset),
    kind: auditBlocked ? 'attention' : spec.toastKind,
    message: auditBlocked
      ? `${spec.summary} — recorded, but the audit writer is in a simulated outage (not logged).`
      : spec.summary,
    correlationId, savedStatus: spec.savedStatus, at: iso(now),
  }

  return {
    ...state,
    records: [...records, ...linkedCreated],
    toasts: [...state.toasts, toast].slice(-6),
    seq: seqBase + offset + 1,
  }
}

function dedupe<T>(xs: T[]): T[] {
  return Array.from(new Set(xs))
}

// ─────────────────────────────────────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────────────────────────────────────

export type GovCommand =
  | { type: 'comment'; recordId: string; body: string; mentions?: string[]; now: number }
  | { type: 'add_update'; recordId: string; body: string; kind?: GovUpdate['kind']; now: number }
  | { type: 'assign'; recordId: string; assignee: string; now: number }
  | { type: 'add_reviewer'; recordId: string; reviewer: string; now: number }
  | { type: 'set_due'; recordId: string; dueDate: string; now: number }
  | { type: 'label'; recordId: string; label: string; now: number }
  | { type: 'unlabel'; recordId: string; label: string; now: number }
  | { type: 'watch'; recordId: string; now: number }
  | { type: 'unwatch'; recordId: string; now: number }
  | { type: 'edit_field'; recordId: string; key: string; value: string; now: number }
  | { type: 'create'; input: NewRecordInput; now: number }
  | { type: 'create_related'; fromId: string; input: NewRecordInput; now: number }
  | { type: 'governed'; recordId: string; action: GovAction; label: string; patch: Partial<GovRecord>; severity?: Severity; denied?: boolean; now: number }
  | { type: 'change_policy'; name: string; now: number }
  | { type: 'restore_policy'; now: number }
  | { type: 'set_simulation'; key: keyof GovSimulations; value: boolean; now: number }
  | { type: 'set_viewer_role'; role: GovRole | null; now: number }
  | { type: 'hydrate_durable'; module: GovModule; records: GovRecord[]; now: number }
  | { type: 'set_durable_status'; status: DurableStatus; now: number }
  | { type: 'share'; recordId: string; recipient: string; constraint: string; now: number }
  | { type: 'reset'; now: number }
  | { type: 'dismiss_toast'; id: string; now: number }

/** Distributive Omit — collapses over each union member, preserving discriminants. */
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never

/** A command minus its `now` stamp — the shape components dispatch. */
export type GovCommandInput = DistributiveOmit<GovCommand, 'now'>

/** Guard a command against the permission model; returns a denial toast if blocked. */
function denyToast(state: GovState, action: GovAction, now: number): GovState {
  const d = evaluatePermission(effectiveRole(state), action)
  const toast: GovToast = {
    id: makeId('toast', state.seq + 1), kind: 'denied',
    message: `Action not permitted: ${d.reason}`,
    savedStatus: 'saved_local_session', at: iso(now),
  }
  return { ...state, toasts: [...state.toasts, toast].slice(-6), seq: state.seq + 1 }
}

export function governanceReducer(state: GovState, cmd: GovCommand): GovState {
  switch (cmd.type) {
    case 'dismiss_toast':
      return { ...state, toasts: state.toasts.filter((t) => t.id !== cmd.id) }

    case 'set_viewer_role':
      return { ...state, viewerRole: cmd.role }

    case 'comment': {
      if (!can(state, 'comment').allowed) return denyToast(state, 'comment', cmd.now)
      const seqBase = state.seq + 1
      const records = state.records.map((r) => {
        if (r.id !== cmd.recordId) return r
        const c: GovComment = {
          id: makeId('cmt', seqBase), actor: state.actor.name, actorRole: effectiveRole(state),
          body: cmd.body, at: iso(cmd.now), mentions: cmd.mentions ?? [],
        }
        return { ...r, comments: [...r.comments, c], updatedAt: iso(cmd.now) }
      })
      return { ...state, records, seq: seqBase + 1 }
    }

    case 'add_update': {
      if (!can(state, 'add_update').allowed) return denyToast(state, 'add_update', cmd.now)
      const seqBase = state.seq + 1
      const records = state.records.map((r) => {
        if (r.id !== cmd.recordId) return r
        const u: GovUpdate = {
          id: makeId('upd', seqBase), actor: state.actor.name, actorRole: effectiveRole(state),
          body: cmd.body, at: iso(cmd.now), kind: cmd.kind ?? 'note', savedStatus: 'saved_local_session',
        }
        return { ...r, updates: [...r.updates, u], updatedAt: iso(cmd.now) }
      })
      return { ...state, records, seq: seqBase + 1 }
    }

    case 'assign': {
      if (!can(state, 'assign').allowed) return denyToast(state, 'assign', cmd.now)
      return patchRecordWithHistory(state, cmd.recordId, { assignee: cmd.assignee }, `assigned to ${cmd.assignee}`, cmd.now)
    }
    case 'add_reviewer': {
      if (!can(state, 'add_reviewer').allowed) return denyToast(state, 'add_reviewer', cmd.now)
      const rec = getRecord(state, cmd.recordId)
      const reviewers = dedupe([...(rec?.reviewers ?? []), cmd.reviewer])
      return patchRecordWithHistory(state, cmd.recordId, { reviewers }, `added reviewer ${cmd.reviewer}`, cmd.now)
    }
    case 'set_due': {
      if (!can(state, 'set_due').allowed) return denyToast(state, 'set_due', cmd.now)
      return patchRecordWithHistory(state, cmd.recordId, { dueDate: cmd.dueDate }, `due date set to ${cmd.dueDate}`, cmd.now)
    }
    case 'label': {
      if (!can(state, 'label').allowed) return denyToast(state, 'label', cmd.now)
      const rec = getRecord(state, cmd.recordId)
      const labels = dedupe([...(rec?.labels ?? []), cmd.label])
      return patchRecordWithHistory(state, cmd.recordId, { labels }, `labeled ${cmd.label}`, cmd.now)
    }
    case 'unlabel': {
      if (!can(state, 'label').allowed) return denyToast(state, 'label', cmd.now)
      const rec = getRecord(state, cmd.recordId)
      const labels = (rec?.labels ?? []).filter((l) => l !== cmd.label)
      return patchRecordWithHistory(state, cmd.recordId, { labels }, `removed label ${cmd.label}`, cmd.now)
    }
    case 'watch': {
      const rec = getRecord(state, cmd.recordId)
      const watchers = dedupe([...(rec?.watchers ?? []), state.actor.name])
      return patchRecordWithHistory(state, cmd.recordId, { watchers }, 'started watching', cmd.now)
    }
    case 'unwatch': {
      const rec = getRecord(state, cmd.recordId)
      const watchers = (rec?.watchers ?? []).filter((w) => w !== state.actor.name)
      return patchRecordWithHistory(state, cmd.recordId, { watchers }, 'stopped watching', cmd.now)
    }
    case 'edit_field': {
      if (!can(state, 'edit').allowed) return denyToast(state, 'edit', cmd.now)
      const rec = getRecord(state, cmd.recordId)
      const fields = { ...(rec?.fields ?? {}), [cmd.key]: cmd.value }
      return patchRecordWithHistory(state, cmd.recordId, { fields }, `edited ${cmd.key}`, cmd.now)
    }

    case 'create': {
      if (!can(state, 'create').allowed) return denyToast(state, 'create', cmd.now)
      return runPipeline(state, {
        action: `create_${cmd.input.module}`, summary: `Created ${cmd.input.title}`,
        targetModule: cmd.input.module, newRecord: cmd.input,
        severity: cmd.input.severity ?? 'info', toastKind: 'success', governed: true,
        savedStatus: 'saved_local_session',
      }, cmd.now)
    }
    case 'create_related': {
      if (!can(state, 'create').allowed) return denyToast(state, 'create', cmd.now)
      const from = getRecord(state, cmd.fromId)
      const input = { ...cmd.input, correlationId: from?.correlationId }
      return runPipeline(state, {
        action: `create_related_${cmd.input.module}`, summary: `Created ${cmd.input.title} (related to ${from?.title ?? cmd.fromId})`,
        targetModule: cmd.input.module, newRecord: input,
        severity: cmd.input.severity ?? 'info', toastKind: 'success', governed: true,
        savedStatus: 'saved_local_session',
      }, cmd.now)
    }

    case 'governed': {
      const d = can(state, cmd.action)
      if (!d.allowed) return denyToast(state, cmd.action, cmd.now)
      const rec = getRecord(state, cmd.recordId)
      return runPipeline(state, {
        action: cmd.action, summary: `${cmd.label}${rec ? ` · ${rec.title}` : ''}`,
        targetModule: rec?.module ?? 'audit', targetId: cmd.recordId, patch: cmd.patch,
        severity: cmd.severity ?? 'medium', toastKind: cmd.denied ? 'denied' : 'success',
        governed: true, savedStatus: 'saved_local_session',
      }, cmd.now)
    }

    case 'share': {
      if (!can(state, 'share').allowed) return denyToast(state, 'share', cmd.now)
      const from = getRecord(state, cmd.recordId)
      return runPipeline(state, {
        action: 'secure_share', summary: `Shared ${from?.title ?? cmd.recordId} with ${cmd.recipient} (${cmd.constraint})`,
        targetModule: 'secure-share',
        newRecord: {
          module: 'secure-share', title: `Share · ${from?.title ?? cmd.recordId}`,
          summary: `Recipient ${cmd.recipient}; constraint: ${cmd.constraint}`,
          statusKind: 'secure', severity: 'info',
          fields: { recipient: cmd.recipient, constraint: cmd.constraint, delivery: 'manual', status: 'active' },
          correlationId: from?.correlationId,
        },
        severity: 'info', toastKind: 'success', governed: true, savedStatus: 'saved_local_session',
      }, cmd.now)
    }

    case 'change_policy': {
      if (!can(state, 'change_policy').allowed) return denyToast(state, 'change_policy', cmd.now)
      const next: GovPolicy = {
        id: makeId('policy', state.seq + 1), name: cmd.name, version: state.policy.version + 1,
        prior: { id: state.policy.id, name: state.policy.name, version: state.policy.version },
      }
      const withPolicy = { ...state, policy: next }
      return runPipeline(withPolicy, {
        action: 'change_policy', summary: `Policy → ${cmd.name} v${next.version}`,
        targetModule: 'permission-review',
        newRecord: {
          module: 'permission-review', title: `Policy change to ${cmd.name} v${next.version}`,
          summary: 'Awaiting review', statusKind: 'attention', severity: 'medium',
          fields: { decision: 'pending', from: `v${state.policy.version}`, to: `v${next.version}` },
        },
        severity: 'medium', toastKind: 'success', governed: true, savedStatus: 'saved_local_session',
      }, cmd.now)
    }
    case 'restore_policy': {
      if (!can(state, 'restore_policy').allowed) return denyToast(state, 'restore_policy', cmd.now)
      if (!state.policy.prior) return state
      const prior = state.policy.prior
      const restored: GovPolicy = { id: prior.id, name: prior.name, version: prior.version }
      const withPolicy = { ...state, policy: restored }
      return runPipeline(withPolicy, {
        action: 'restore_policy', summary: `Policy restored to ${prior.name} v${prior.version}`,
        targetModule: 'audit', severity: 'medium', toastKind: 'attention', governed: true,
        savedStatus: 'saved_local_session',
      }, cmd.now)
    }

    case 'set_simulation':
      return { ...state, simulations: { ...state.simulations, [cmd.key]: cmd.value } }

    case 'hydrate_durable': {
      // Merge real durable rows for a module: drop that module's design-sample
      // and prior durable rows, keep any local-session records created this
      // session, and add the durable rows. Real data supersedes the sample.
      const kept = state.records.filter((r) => r.module !== cmd.module || r.origin === 'local_session')
      return { ...state, records: [...kept, ...cmd.records] }
    }

    case 'set_durable_status':
      return { ...state, durable: cmd.status }

    case 'reset':
      return buildInitialState({ actor: state.actor })

    default:
      return state
  }
}

/** Patch a record and append a history line, without the full governed pipeline. */
function patchRecordWithHistory(
  state: GovState, recordId: string, patch: Partial<GovRecord>, action: string, now: number,
): GovState {
  const seqBase = state.seq + 1
  let touched = false
  const records = state.records.map((r) => {
    if (r.id !== recordId) return r
    touched = true
    const hist: GovHistoryEntry = {
      id: makeId('hist', seqBase), actor: state.actor.name, action, at: iso(now),
      savedStatus: 'saved_local_session',
    }
    return { ...r, ...patch, updatedAt: iso(now), history: [...r.history, hist] }
  })
  if (!touched) return state
  return { ...state, records, seq: seqBase + 1 }
}

// ─────────────────────────────────────────────────────────────────────────────
// Seed (design sample — deterministic; labeled origin: 'design_sample')
// ─────────────────────────────────────────────────────────────────────────────

const SEED_BASE = Date.parse('2026-07-10T15:42:18.000Z')

export const DEFAULT_SCOPE: GovScope = {
  organization: { id: 'org_square_code', name: 'Square Code' },
  project: { id: 'proj_olivium', name: 'Meridian Utilities — Olivium Workspace Portal' },
  environment: { id: 'env_olivium_demo', name: 'Demo / Preview' },
  portal: { id: 'portal_2', name: 'Olivium Workspace Portal' },
}

export const DEFAULT_ACTOR = { id: 'usr_jordan', name: 'Jordan Gaither', role: 'Operator' as GovRole }

interface SeedSpec {
  module: GovModule
  title: string
  summary: string
  statusKind: StatusKind
  severity?: Severity
  fields?: Record<string, string>
  correlationId: string
  durable?: boolean
  minutesAgo: number
}

/** Deterministic seed: ≥6 records per module, with cross-module correlations. */
const SEED: SeedSpec[] = [
  // Correlation story A — policy change v2→v3
  { module: 'audit', title: 'change policy', summary: 'Invite-only policy raised v2 → v3', statusKind: 'identity', correlationId: 'corr_A', minutesAgo: 120, fields: { result: 'allowed', source_ip: '10.0.4.2' } },
  { module: 'receipts', title: 'Receipt · change policy', summary: 'Proof for policy v3 publish', statusKind: 'verified', correlationId: 'corr_A', minutesAgo: 120, fields: { receipt_status: 'issued', bundled: 'true' } },
  { module: 'integrity', title: 'Block 000418', summary: 'Chained over policy v3 publish', statusKind: 'structure', correlationId: 'corr_A', minutesAgo: 120, fields: { chain_status: 'linked' } },
  { module: 'permission-review', title: 'Policy change to Invite-only v3', summary: 'Approved by Governance Admin', statusKind: 'verified', correlationId: 'corr_A', minutesAgo: 118, fields: { decision: 'approved' } },
  // Correlation story B — grant revoked
  { module: 'audit', title: 'revoke grant', summary: 'Revoked access grant for contractor', statusKind: 'denied', severity: 'high', correlationId: 'corr_B', minutesAgo: 60, fields: { result: 'allowed', source_ip: '10.0.4.9' } },
  { module: 'receipts', title: 'Receipt · revoke grant', summary: 'Proof for grant revocation', statusKind: 'verified', correlationId: 'corr_B', minutesAgo: 60, fields: { receipt_status: 'issued' } },
  { module: 'integrity', title: 'Block 000419', summary: 'Chained over grant revocation', statusKind: 'structure', correlationId: 'corr_B', minutesAgo: 60, fields: { chain_status: 'linked' } },
  { module: 'risks', title: 'Risk · revoked grant reuse', summary: 'Revoked token seen once post-revocation', statusKind: 'attention', severity: 'high', correlationId: 'corr_B', minutesAgo: 58, fields: { risk_status: 'open' } },
  { module: 'access-logs', title: 'Session · contractor', summary: 'Terminated after revocation', statusKind: 'denied', correlationId: 'corr_B', minutesAgo: 57, fields: { decision: 'terminated', device: 'unknown' } },
  // Correlation story C — secure share
  { module: 'audit', title: 'secure share', summary: 'Shared audit bundle with external reviewer', statusKind: 'identity', correlationId: 'corr_C', minutesAgo: 30, fields: { result: 'allowed' } },
  { module: 'receipts', title: 'Receipt · secure share', summary: 'Proof for evidence bundle share', statusKind: 'verified', correlationId: 'corr_C', minutesAgo: 30, fields: { receipt_status: 'issued', bundled: 'true' } },
  { module: 'integrity', title: 'Block 000414', summary: 'Chained over evidence bundle share', statusKind: 'structure', correlationId: 'corr_C', minutesAgo: 30, fields: { chain_status: 'linked' } },
  { module: 'secure-share', title: 'Share · Q3 audit bundle', summary: 'External reviewer; view-only; 7-day expiry', statusKind: 'secure', correlationId: 'corr_C', minutesAgo: 30, fields: { recipient: 'reviewer@external.example', constraint: 'view_only_7d', status: 'active', delivery: 'manual' } },
  // Standalone records to reach ≥6 per module
  { module: 'audit', title: 'trust device', summary: 'Operator trusted a new device', statusKind: 'identity', correlationId: 'corr_D', minutesAgo: 200, fields: { result: 'allowed' } },
  { module: 'audit', title: 'verify chain', summary: 'Manual integrity verification run', statusKind: 'verified', correlationId: 'corr_E', minutesAgo: 240, fields: { result: 'allowed' } },
  { module: 'audit', title: 'issue grant', summary: 'Issued scoped access to collaborator', statusKind: 'identity', correlationId: 'corr_F', durable: false, minutesAgo: 300, fields: { result: 'allowed' } },
  { module: 'integrity', title: 'Block 000417', summary: 'Genesis-linked baseline block', statusKind: 'verified', correlationId: 'corr_G', minutesAgo: 400, fields: { chain_status: 'verified' } },
  { module: 'integrity', title: 'Block 000416', summary: 'Chained over device trust', statusKind: 'structure', correlationId: 'corr_D', minutesAgo: 200, fields: { chain_status: 'linked' } },
  { module: 'integrity', title: 'Block 000415', summary: 'Chained over grant issuance', statusKind: 'structure', correlationId: 'corr_F', minutesAgo: 300, fields: { chain_status: 'linked' } },
  { module: 'receipts', title: 'Receipt · issue grant', summary: 'Proof for scoped access issuance', statusKind: 'verified', correlationId: 'corr_F', minutesAgo: 300, fields: { receipt_status: 'issued' } },
  { module: 'receipts', title: 'Receipt · verify chain', summary: 'Proof for manual verification', statusKind: 'verified', correlationId: 'corr_E', minutesAgo: 240, fields: { receipt_status: 'issued' } },
  { module: 'receipts', title: 'Receipt · trust device', summary: 'Proof for device trust', statusKind: 'verified', correlationId: 'corr_D', minutesAgo: 200, fields: { receipt_status: 'issued' } },
  { module: 'risks', title: 'Risk · stale reviewer', summary: 'Reviewer inactive 14 days on open review', statusKind: 'attention', severity: 'medium', correlationId: 'corr_H', minutesAgo: 500, fields: { risk_status: 'open' } },
  { module: 'risks', title: 'Risk · broad share scope', summary: 'Share scope wider than policy suggests', statusKind: 'attention', severity: 'medium', correlationId: 'corr_C', minutesAgo: 29, fields: { risk_status: 'open' } },
  { module: 'risks', title: 'Risk · env drift', summary: 'Demo env config drifted from baseline', statusKind: 'neutral', severity: 'low', correlationId: 'corr_I', minutesAgo: 600, fields: { risk_status: 'monitoring' } },
  { module: 'risks', title: 'Risk · resolved lockout', summary: 'Repeated lockout — resolved', statusKind: 'verified', severity: 'low', correlationId: 'corr_J', minutesAgo: 700, fields: { risk_status: 'resolved' } },
  { module: 'risks', title: 'Risk · policy drift', summary: 'Grant scope lags behind policy v3', statusKind: 'attention', severity: 'medium', correlationId: 'corr_A', minutesAgo: 117, fields: { risk_status: 'monitoring' } },
  { module: 'access-logs', title: 'Session · operator', summary: 'Active operator session, trusted device', statusKind: 'verified', correlationId: 'corr_K', minutesAgo: 15, fields: { decision: 'allowed', device: 'trusted' } },
  { module: 'access-logs', title: 'Session · reviewer', summary: 'Active reviewer session', statusKind: 'verified', correlationId: 'corr_L', minutesAgo: 45, fields: { decision: 'allowed', device: 'trusted' } },
  { module: 'access-logs', title: 'Session · challenged login', summary: 'Step-up challenge issued', statusKind: 'attention', correlationId: 'corr_M', minutesAgo: 90, fields: { decision: 'challenged', device: 'new' } },
  { module: 'access-logs', title: 'Session · suspended', summary: 'Session suspended pending review', statusKind: 'attention', correlationId: 'corr_H', minutesAgo: 120, fields: { decision: 'suspended', device: 'trusted' } },
  { module: 'access-logs', title: 'Session · expired', summary: 'Idle session expired', statusKind: 'neutral', correlationId: 'corr_N', minutesAgo: 800, fields: { decision: 'expired', device: 'trusted' } },
  { module: 'secure-share', title: 'Share · portal readiness', summary: 'Internal team; edit; no expiry', statusKind: 'secure', correlationId: 'corr_O', minutesAgo: 150, fields: { recipient: 'team@square.example', constraint: 'edit_internal', status: 'active', delivery: 'manual' } },
  { module: 'secure-share', title: 'Share · revoked link', summary: 'Revoked after recipient left', statusKind: 'denied', correlationId: 'corr_P', minutesAgo: 900, fields: { recipient: 'former@external.example', constraint: 'view_only', status: 'revoked', delivery: 'manual' } },
  { module: 'secure-share', title: 'Share · receipt bundle', summary: 'Auditor; view-only; 30-day', statusKind: 'secure', correlationId: 'corr_Q', minutesAgo: 1000, fields: { recipient: 'auditor@external.example', constraint: 'view_only_30d', status: 'active', delivery: 'manual' } },
  { module: 'secure-share', title: 'Share · expiring soon', summary: 'External reviewer; expires in 2 days', statusKind: 'attention', correlationId: 'corr_R', minutesAgo: 1100, fields: { recipient: 'temp@external.example', constraint: 'view_only_2d', status: 'expiring', delivery: 'manual' } },
  { module: 'secure-share', title: 'Share · integrity export', summary: 'Auditor; view-only; single-use', statusKind: 'secure', correlationId: 'corr_E', minutesAgo: 235, fields: { recipient: 'auditor@external.example', constraint: 'view_only_single', status: 'active', delivery: 'manual' } },
  { module: 'permission-review', title: 'Elevate contributor → reviewer', summary: 'Requested by team lead', statusKind: 'attention', severity: 'medium', correlationId: 'corr_S', minutesAgo: 180, fields: { decision: 'pending' } },
  { module: 'permission-review', title: 'Grant operator to on-call', summary: 'Time-boxed; expires end of rotation', statusKind: 'attention', severity: 'medium', correlationId: 'corr_T', minutesAgo: 220, fields: { decision: 'pending', expires: 'rotation_end' } },
  { module: 'permission-review', title: 'Deny external admin request', summary: 'Denied — outside policy', statusKind: 'denied', correlationId: 'corr_U', minutesAgo: 260, fields: { decision: 'denied' } },
  { module: 'permission-review', title: 'Reviewer scope change', summary: 'Approved with reason + expiry', statusKind: 'verified', correlationId: 'corr_V', minutesAgo: 300, fields: { decision: 'approved', reason: 'audit season', expires: '2026-09-01' } },
  { module: 'permission-review', title: 'Revoke stale approver', summary: 'Approved revocation', statusKind: 'verified', correlationId: 'corr_W', minutesAgo: 340, fields: { decision: 'approved' } },
]

function seedRecord(spec: SeedSpec, n: number): GovRecord {
  const at = iso(SEED_BASE - spec.minutesAgo * 60_000)
  return {
    id: makeId(RECORD_PREFIX[spec.module], n), module: spec.module, correlationId: spec.correlationId,
    title: spec.title, summary: spec.summary, status: KIND_STATUS[spec.statusKind], statusKind: spec.statusKind,
    severity: spec.severity ?? 'info', actor: DEFAULT_ACTOR.name, actorRole: 'Operator',
    createdAt: at, updatedAt: at,
    origin: 'design_sample', savedStatus: spec.durable ? 'saved_durable' : 'saved_local_session',
    fields: spec.fields ?? {}, labels: [], assignee: null, reviewers: [], watchers: [], dueDate: null,
    comments: [], updates: [], history: [{ id: makeId('hist', 90000 + n), actor: DEFAULT_ACTOR.name, action: 'seeded', at, savedStatus: 'saved_local_session' }],
    links: [],
  }
}

export function buildInitialState(opts?: { actor?: { id: string; name: string; role: GovRole } }): GovState {
  const records = SEED.map((s, i) => seedRecord(s, 1000 + i))
  // Cross-link records that share a correlation id.
  const byCorr = new Map<string, string[]>()
  for (const r of records) {
    byCorr.set(r.correlationId, [...(byCorr.get(r.correlationId) ?? []), r.id])
  }
  const linked = records.map((r) => ({
    ...r, links: (byCorr.get(r.correlationId) ?? []).filter((id) => id !== r.id),
  }))
  return {
    scope: DEFAULT_SCOPE,
    policy: {
      id: 'policy_invite_only_v3', name: 'Invite-only', version: 3,
      prior: { id: 'policy_invite_only_v2', name: 'Invite-only', version: 2 },
    },
    records: linked,
    toasts: [],
    simulations: { auditWriterOutage: false, integrityMismatch: false, policyConflict: false },
    actor: opts?.actor ?? DEFAULT_ACTOR,
    viewerRole: null,
    durable: null,
    seq: 2000,
  }
}
